import { useEffect, useRef, useState } from "react";

// We load Leaflet from CDN to avoid bundler config complexity.
// In a CRA app, you'd: npm install leaflet react-leaflet
// and use <MapContainer> from react-leaflet instead.

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
// Leaflet.markercluster: groups nearby live buses into a numbered cluster so
// the map never degenerates into an unreadable pile of overlapping badges.
const CLUSTER_CSS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.css";
const CLUSTER_DEFAULT_CSS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/MarkerCluster.Default.css";
const CLUSTER_JS = "https://unpkg.com/leaflet.markercluster@1.5.3/dist/leaflet.markercluster.js";

let leafletReady = false;
let leafletCallbacks = [];

function loadLeaflet(cb) {
  if (leafletReady) return cb();
  leafletCallbacks.push(cb);
  if (leafletCallbacks.length > 1) return; // already loading

  // CSS
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = LEAFLET_CSS;
  document.head.appendChild(link);
  const cl = document.createElement("link");
  cl.rel = "stylesheet";
  cl.href = CLUSTER_CSS;
  document.head.appendChild(cl);
  if (CLUSTER_DEFAULT_CSS) {
    const cld = document.createElement("link");
    cld.rel = "stylesheet";
    cld.href = CLUSTER_DEFAULT_CSS;
    document.head.appendChild(cld);
  }

  // JS
  const script = document.createElement("script");
  script.src = LEAFLET_JS;
  script.onload = () => {
    // Leaflet loaded — now load markercluster (must come after Leaflet).
    const cs = document.createElement("script");
    cs.src = CLUSTER_JS;
    cs.onload = () => {
      leafletReady = true;
      leafletCallbacks.forEach((fn) => fn());
      leafletCallbacks = [];
    };
    document.head.appendChild(cs);
  };
  document.head.appendChild(script);
}

export default function BusMap({ stops = [], vehicles = [], taxis = [], userLocation, selectedStop, onStopClick, onVehicleClick, onTaxiClick, scrollWheelZoom = true, onMapViewChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const stopLayerRef = useRef(null);
  const vehicleLayerRef = useRef(null);
  const userLayerRef = useRef(null);
  const taxiLayerRef = useRef(null);
  const liveTaxiLayerRef = useRef(null);

  // Initialise map once Leaflet is loaded
  useEffect(() => {
    loadLeaflet(() => {
      if (mapRef.current || !containerRef.current) return;
      const L = window.L;

      const map = L.map(containerRef.current, {
        center: [50.3714, -4.1425],
        zoom: 13,
        zoomControl: true,
        scrollWheelZoom,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);

      // Stop markers: cluster group too. All Plymouth stops crowd at the
      // initial zoom, so grouping them into numbered bubbles keeps the main
      // map readable; they spread out as you zoom in.
      stopLayerRef.current = (L.markerClusterGroup ? L.markerClusterGroup({
        maxClusterRadius: 55,
        disableClusteringAtZoom: 16,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        iconCreateFunction: function (cluster) {
          const n = cluster.getChildCount();
          return L.divIcon({ html: n, className: "stop-cluster", iconSize: [46, 46] });
        },
      }) : L.layerGroup()).addTo(map);
      // Live buses — plain layer group so every bus shows as its own marker
      // (no clustering), each individually visible on the map.
      vehicleLayerRef.current = L.layerGroup().addTo(map);
      userLayerRef.current = L.layerGroup().addTo(map);
      taxiLayerRef.current = L.layerGroup().addTo(map);
      liveTaxiLayerRef.current = L.layerGroup().addTo(map);
      mapRef.current = map;
    });
  }, []);

  // Update stop markers
  useEffect(() => {
    if (!mapRef.current || !stopLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    stopLayerRef.current.clearLayers();

    stops.forEach((stop) => {
      const isSelected = selectedStop?.id === stop.id;
      // On-map marker: Alin's bus-stop pin (white teardrop map-pin with a black
      // bus silhouette + headlights). Non-selected = black pin, discreet.
      // Selected = larger red teardrop that pops out of the map.
      const w = isSelected ? 42 : 34;
      const h = Math.round((w * 320) / 240); // keep 240x320 aspect
      const pinFill = isSelected ? "#D32F2F" : "#000000";
      const icon = L.divIcon({
        className: "stop-marker-wrap",
        html: `
          <div style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.45));">
            <svg width="${w}" height="${h}" viewBox="0 0 240 320" xmlns="http://www.w3.org/2000/svg">
              <!-- Map pin (black, red when selected) -->
              <path fill="${pinFill}" d="M120 8 C68 8 28 48 28 100 C28 152 88 230 120 312 C152 230 212 152 212 100 C212 48 172 8 120 8 Z"/>
              <!-- Inner white circle -->
              <circle cx="120" cy="100" r="68" fill="#ffffff"/>
              <!-- Bus body -->
              <rect x="78" y="62" width="84" height="62" rx="10" ry="10" fill="#000000"/>
              <!-- Windows -->
              <rect x="86" y="70" width="30" height="28" rx="2" ry="2" fill="#ffffff"/>
              <rect x="124" y="70" width="30" height="28" rx="2" ry="2" fill="#ffffff"/>
              <!-- Headlights -->
              <circle cx="96" cy="112" r="6" fill="#ffffff"/>
              <circle cx="144" cy="112" r="6" fill="#ffffff"/>
              <!-- Bumper / lower bar -->
              <rect x="86" y="122" width="68" height="6" rx="2" ry="2" fill="#000000"/>
              <!-- Wheels -->
              <rect x="90" y="128" width="16" height="10" rx="4" ry="4" fill="#000000"/>
              <rect x="134" y="128" width="16" height="10" rx="4" ry="4" fill="#000000"/>
            </svg>
          </div>
        `,
        iconSize: [w, h],
        iconAnchor: [w / 2, h], // bottom tip = the precise map point
      });

      const marker = L.marker([stop.lat, stop.lng], { icon })
        .bindPopup(`
          <strong style="font-size:13px;">${stop.name}</strong><br/>
          ${stop.distanceKm !== undefined ? `<span style="font-size:12px;color:#003087;font-weight:600;">${(stop.distanceKm * 1000).toFixed(0)}m away</span><br/>` : ""}
          <span style="font-size:11px;color:#555;">Routes: ${stop.routes.join(", ") || "—"}</span>
        `, { maxWidth: 220 })
        .on("click", () => onStopClick && onStopClick(stop));

      stopLayerRef.current.addLayer(marker);
    });
  }, [stops, selectedStop, onStopClick]);

  // Update live vehicle markers
  useEffect(() => {
    if (!mapRef.current || !vehicleLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    vehicleLayerRef.current.clearLayers();

    vehicles.forEach((v) => {
      // Live bus marker: a green teardrop map-pin with the route number
      // centred inside (Alin's pin design). Stays upright so the number is
      // always readable — distinct from the blue circular bus stops and the
      // yellow taxi squares.
      const size = 40; // rendered width of pin head
      const pinH = Math.round(size * 280 / 220);
      const scale = size / 220;
      // white disc: cy=104 r=56 in the 220-wide viewbox -> vertical band 48..160
      const discTop = Math.round(48 * scale);
      const discH = Math.round(112 * scale);
      const textSize = Math.round(size * 0.36);
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          filter:drop-shadow(0 2px 4px rgba(0,0,0,0.45));
          width:${size}px; height:${pinH}px;
          position:relative;
        ">
          <svg width="${size}" height="${pinH}" viewBox="0 0 220 280" xmlns="http://www.w3.org/2000/svg">
            <!-- Ground shadow -->
            <ellipse cx="110" cy="268" rx="40" ry="7.5" fill="#000" opacity="0.3"/>
            <!-- Pin pointer (green) -->
            <path d="M78 168 L110 248 L142 168 Z" fill="#1f9d4d"/>
            <!-- Pin head (green teardrop) -->
            <path d="M110 18 C66 18 30 52 30 112 C30 150 60 188 110 224 C160 188 190 150 190 112 C190 52 154 18 110 18 Z" fill="#1f9d4d" stroke="#ffffff" stroke-width="4"/>
            <!-- Inner white disc for the route number -->
            <circle cx="110" cy="104" r="56" fill="#ffffff"/>
          </svg>
          <div style="
            position:absolute; left:3px; right:3px;
            top:${discTop}px; height:${discH}px;
            display:flex; align-items:center; justify-content:center;
            color:#0b6b33; font-size:${textSize}px; line-height:1;
            font-weight:900; letter-spacing:-0.5px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          ">${v.route || "🚌"}</div>
        </div>`,
        iconSize: [size, pinH],
        // pin tip ~ y=236 in the viewbox -> anchor near the bottom point
        iconAnchor: [Math.round(size / 2), Math.round(236 * scale)],
      });

      L.marker([v.lat, v.lng], { icon })
        .bindPopup(`
          <strong style="font-size:14px;">Route ${v.route}</strong><br/>
          <span style="font-size:12px;">${v.destination || ""}</span><br/>
          <span style="font-size:11px;color:#888;">${v.vehicleRef}</span>
        `)
        .on("click", () => onVehicleClick && onVehicleClick(v))
        .addTo(vehicleLayerRef.current);
    });
  }, [vehicles, onVehicleClick]);

  // Update CLEAR taxi markers (from TaxiCRM, overlaid on the bus map)
  useEffect(() => {
    if (!mapRef.current || !taxiLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    taxiLayerRef.current.clearLayers();

    (taxis || []).forEach((t) => {
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:28px; height:28px;
          background:#F5B800;
          border:2.5px solid #1a1a1a;
          border-radius:8px;
          display:flex; align-items:center; justify-content:center;
          font-size:14px; line-height:1;
          box-shadow:0 2px 6px rgba(0,0,0,0.45);
        ">🚕</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });

      L.marker([t.lat, t.lng], { icon })
        .bindPopup(`<strong style="font-size:13px;">🚕 CLEAR taxi</strong><br/><span style="font-size:11px;color:#888;">driver #${t.driverId ?? "—"}</span>`)
        .on("click", () => onTaxiClick && onTaxiClick(t))
        .addTo(taxiLayerRef.current);
    });
  }, [taxis, onTaxiClick]);

  // Update CLEAR taxi markers from TaxiCRM (separate layer so it never
  // conflicts with the nearest-taxi ETA layer below).
  useEffect(() => {
    if (!mapRef.current || !liveTaxiLayerRef.current) return;
    const L = window.L;
    if (!L) return;
    liveTaxiLayerRef.current.clearLayers();

    (Array.isArray(taxis) ? taxis : []).forEach((t) => {
      const lat = Number(t.lat);
      const lng = Number(t.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const icon = L.divIcon({
        className: "",
        html: `<div style="
          width:26px; height:26px;
          background:#F5B800;
          border:2.5px solid #1a1a1a;
          border-radius:8px;
          display:flex; align-items:center; justify-content:center;
          font-size:13px;
          box-shadow:0 2px 6px rgba(0,0,0,0.45);
        ">🚕</div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });

      L.marker([lat, lng], { icon })
        .bindPopup(`<strong style="font-size:13px;">🚕 CLEAR taxi</strong>`)
        .on("click", () => onTaxiClick && onTaxiClick(t))
        .addTo(liveTaxiLayerRef.current);
    });
  }, [taxis, onTaxiClick]);

  // Update user location marker
  useEffect(() => {
    if (!mapRef.current || !userLayerRef.current) return;
    const L = window.L;
    if (!L || !userLocation) return;
    userLayerRef.current.clearLayers();

    // Google-style location marker: compact rectangular info card above the
    // blue GPS dot. The card is a small, sharp-cornered square (not a big wide
    // banner) holding: street · Next bus stop · Taxi Services. The blue dot +
    // halo stay at the exact GPS point; the re-centre button brings the user
    // back when they pan away.
    const lblStopId = "lb-stop-" + Math.round(Math.random() * 1e9);
    const lblTaxiId = "lb-taxi-" + Math.round(Math.random() * 1e9);
    const card = L.divIcon({
      className: "",
      html: `<div style="display:flex;flex-direction:column;align-items:center;">
        <div style="background:#fff;border:1px solid rgba(0,0,0,0.14);border-radius:4px;box-shadow:0 1px 4px rgba(0,0,0,0.3);
             padding:3px 6px;min-width:120px;max-width:min(62vw,170px);text-align:center;font-family:-apple-system,Segoe UI,Roboto,sans-serif;">
          <div style="font-size:10px;font-weight:800;color:#0074D9;line-height:1.2;">YOU ARE HERE</div>
          <div style="font-size:10px;color:#334155;font-weight:600;line-height:1.2;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;" id="lb-street"></div>
          <div style="font-size:10px;color:#0b6b33;font-weight:700;line-height:1.2;white-space:nowrap;" id="${lblStopId}"></div>
          <div style="font-size:10px;color:#7a4a00;font-weight:600;line-height:1.2;white-space:nowrap;" id="${lblTaxiId}"></div>
        </div>
        <!-- blue dot -->
        <div style="width:16px;height:16px;margin-top:1px;border-radius:50%;background:#0074D9;border:2.5px solid #fff;box-shadow:0 0 0 2px rgba(0,116,217,0.35),0 0 4px rgba(0,0,0,0.4);"></div>
      </div>`,
      iconSize: [0, 0],
      iconAnchor: [0, 0],
    });
    const mLoc = L.marker([userLocation.lat, userLocation.lng], { icon: card })
      .addTo(userLayerRef.current)
      .bindPopup("You are here", { closeButton: false });
    // subtle halo ring around the GPS point
    L.circle([userLocation.lat, userLocation.lng], { radius: 26, color: "rgba(0,116,217,0.2)", weight: 2, fill: false })
      .addTo(userLayerRef.current);

    // Helper: distance formatting (imperial, as the user asked).
    const fmtDist = (km) => {
      const mi = km / 1.60934;
      return mi < 0.1 ? Math.round(mi * 5280) + " ft" : mi.toFixed(1) + " mi";
    };
    const fmtMin = (mi) => Math.max(1, Math.round(mi / (16 / 60))); // rough urban ~16 mph
    // Haversine straight-line (used only for quick street/nearest selection + fallback).
    const havMi = (a, b) => {
      const R = 3958.8, dLat = ((b.lat - a.lat) * Math.PI) / 180, dLng = ((b.lng - a.lng) * Math.PI) / 180;
      const s = Math.sin(dLat / 2) ** 2 + Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
    };

    // Populate street name via Nominatim (small reverse geocode), then fill the
    // two dynamic lines (next stop distance & taxi services) with OSRM routing.
    const setTxt = (id, txt) => { const el = document.getElementById(id); if (el) el.textContent = txt; };
    // 1) Street
    fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&zoom=18&addressdetails=1&lat=${encodeURIComponent(userLocation.lat)}&lon=${encodeURIComponent(userLocation.lng)}`)
      .then((r) => r.json())
      .then((d) => { const a = (d && d.address) || {}; setTxt("lb-street", a.road || a.pedestrian || a.footway || ""); })
      .catch(() => {});
    // 2) Next bus stop (selected stop) — real routing via OSRM (like GPS).
    const osrmDist = (lt, lg) => fetch(
      `https://router.project-osrm.org/route/v1/driving/${userLocation.lng},${userLocation.lat};${lg},${lt}?overview=false`
    ).then((r) => r.json()).then((d) => (d && d.routes && d.routes[0]) ? d.routes[0].distance / 1609.34 : null).catch(() => null);

    if (selectedStop && Number.isFinite(+selectedStop.lat)) {
      setTxt(lblStopId, "…");
      osrmDist(+selectedStop.lat, +selectedStop.lng).then((mi) => {
        if (mi == null) { setTxt(lblStopId, ""); return; }
        setTxt(lblStopId, "Next bus stop · " + (mi < 0.1 ? Math.round(mi * 5280) + " ft" : mi.toFixed(1) + " mi") + " · " + fmtMin(mi) + " min");
      });
    }
    // 3) Taxi Services (nearest CLEAR taxi by straight line, then route).
    if (Array.isArray(taxis) && taxis.length) {
      let best = null, bd = Infinity;
      for (const t of taxis) {
        if (!Number.isFinite(+t.lat) || !Number.isFinite(+t.lng)) continue;
        const m = havMi(userLocation, { lat: +t.lat, lng: +t.lng });
        if (m < bd) { bd = m; best = t; }
      }
      if (best) {
        setTxt(lblTaxiId, "…");
        osrmDist(+best.lat, +best.lng).then((mi) => {
          if (mi == null) { setTxt(lblTaxiId, ""); return; }
          setTxt(lblTaxiId, "Taxi ~" + (mi < 0.1 ? Math.round(mi * 5280) + " ft" : mi.toFixed(1) + " mi") + " · " + fmtMin(mi) + " min away");
        });
      }
    }

    // Find the nearest bus stop to the user, so we can zoom exactly onto it.
    // "Enable location" should put the stop right beside the user in focus.
    let target = null;
    let nearestDist = Infinity;
    if (Array.isArray(stops) && stops.length > 0) {
      for (const s of stops) {
        const d = typeof s.distanceKm === "number" ? s.distanceKm : Infinity;
        if (d < nearestDist) {
          nearestDist = d;
          target = s;
        }
      }
    }

    if (target) {
      // Zoom onto the nearest stop: centre the map there, tight enough to
      // make the stop pin the clear focal point.
      mapRef.current.flyTo([target.lat, target.lng], 17, { duration: 1.0 });
    } else {
      // No stops nearby yet — fall back to framing 400m around the user.
      mapRef.current.flyTo([userLocation.lat, userLocation.lng], 15, { duration: 1.0 });
    }
  }, [userLocation, stops, selectedStop, taxis]);

  // Pan to selected stop
  useEffect(() => {
    if (!mapRef.current || !selectedStop) return;
    mapRef.current.flyTo([selectedStop.lat, selectedStop.lng], 16, { duration: 0.8 });
  }, [selectedStop]);

  // Report the current visible map centre whenever the map is panned/zoomed,
  // so the parent can re-fetch/re-sort the stops "nearest to the screen centre"
  // instead of pinning them to the user's GPS fix (Alin's request).
  const viewRef = useRef(onMapViewChange);
  viewRef.current = onMapViewChange;
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const report = () => {
      const c = map.getCenter();
      viewRef.current && viewRef.current({ lat: c.lat, lng: c.lng, zoom: map.getZoom() });
    };
    map.on("moveend", report);
    // fire once on mount so the initial centre is known
    report();
    return () => { map.off("moveend", report); };
  }, []);

  const locateMe = () => {
    if (!mapRef.current || !userLocation) return;
    mapRef.current.flyTo([userLocation.lat, userLocation.lng], Math.max(mapRef.current.getZoom(), 16), { duration: 0.7 });
  };

  return (
    <div style={{ position: "relative", width: "100%", marginBottom: "0.75rem" }}>
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "260px",
          borderRadius: "12px",
          overflow: "hidden",
          border: "0.5px solid var(--color-border-tertiary)",
        }}
      />
      {userLocation && (
        <button
          type="button"
          onClick={locateMe}
          aria-label="Locate me"
          title="Locate me"
          style={{
            position: "absolute",
            right: "10px",
            bottom: "14px",
            width: "34px",
            height: "34px",
            borderRadius: "8px",
            background: "#ffffff",
            border: "2px solid rgba(0,0,0,0.18)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: 0,
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="2.6" fill="#1a73e8"/>
            <path d="M12 3 a9 9 0 0 1 9 9 h-2.4 a6.6 6.6 0 0 0 -6.6 -6.6 Z" fill="#1a73e8"/>
            <path d="M12 21 a9 9 0 0 1 -9 -9 h2.4 a6.6 6.6 0 0 0 6.6 6.6 Z" fill="#1a73e8"/>
            <line x1="12" y1="2" x2="12" y2="5" stroke="#1a73e8" strokeWidth="1.6" strokeLinecap="round"/>
          </svg>
        </button>
      )}
    </div>
  );
}
